import { useState } from 'react'
import { ArrowClockwiseIcon, PlusIcon, FileTextIcon, WarningCircleIcon } from '@phosphor-icons/react'
import {
    ScoopText,
    ScoopChip,
    ScoopButton,
    ScoopColumn,
    ScoopRow,
    ScoopDialog,
    ScoopSpinner,
    ScoopLinearProgress,
    ScoopAlert,
    ScoopTooltip,
    useToast,
} from '../../../../ui'
import {
    useGroups,
    useGroupConfig,
    useRerunGroup,
    isActiveGroupStatus,
} from '../../../../../api/hooks/domainIntelligence/useGroups'
import { formatDate } from '../../utils/formatDate'
import styles from './GroupRunsPanel.module.scss'

const STATUS_COLORS = {
    queued: 'default',
    PENDING: 'default',
    RUNNING: 'info',
    running: 'info',
    COMPLETE: 'success',
    COMPLETED: 'success',
    FAILED: 'error',
    PARTIAL: 'warning',
}

/**
 * Investigation-group runs — the ONE real run method, shared with the CLI. A
 * group is any named scope of entities (a retail district, a set of agents, a
 * hotel portfolio — whatever the group config defines). Lists prior groups and
 * offers "Run again", which launches the same stored config through the same
 * pipeline as a new cycle (backend rerunGroup).
 *
 * Launching from here executes on AWS Batch rather than in-process on evo —
 * same code, different compute — so the confirm dialog says so until a proving
 * run has demonstrated parity (the August operator-independence gate).
 */
export const GroupRunsPanel = ({ workspaceID, onNewRun }) => {
    const toast = useToast()
    const { groups, loadingGroups, groupsError } = useGroups(workspaceID)
    const rerunMutation = useRerunGroup(workspaceID)
    const [confirmGroup, setConfirmGroup] = useState(null)
    const [viewConfigGroup, setViewConfigGroup] = useState(null)
    const { groupConfig, loadingGroupConfig, groupConfigError } = useGroupConfig(
        workspaceID,
        viewConfigGroup?.groupId || null
    )

    const handleConfirmRerun = () => {
        if (!confirmGroup) return
        rerunMutation.mutate(confirmGroup.groupId, {
            onSuccess: (response) => {
                toast.success(`Run started: ${response?.groupId}`)
                setConfirmGroup(null)
            },
            onError: (error) => {
                toast.error(error?.message || 'Failed to start run')
                setConfirmGroup(null)
            },
        })
    }

    if (loadingGroups) {
        return (
            <div className={styles.state}>
                <ScoopSpinner size={18} />
            </div>
        )
    }

    if (groupsError) {
        return (
            <div className={styles.errorRow}>
                <WarningCircleIcon size={16} className={styles.errorIcon} />
                <ScoopText className={styles.error}>
                    Could not load runs: {groupsError.message}
                </ScoopText>
            </div>
        )
    }

    return (
        <div className={styles.container}>
            <div className={styles.headerRow}>
                <ScoopText className={styles.title}>Launch</ScoopText>
                <ScoopButton
                    variant="grey"
                    size="xs"
                    onClick={() => onNewRun?.('')}
                    title="Author a new run configuration and launch it"
                >
                    <PlusIcon size={14} />
                    New
                </ScoopButton>
            </div>
            <ScoopColumn className={styles.list}>
                {groups.slice(0, 8).map((group) => {
                    const active = isActiveGroupStatus(group.status)
                    return (
                        <div key={group.groupId} className={styles.row}>
                            <ScoopColumn className={styles.rowInfo}>
                                <ScoopText className={styles.groupName}>
                                    {group.groupName || group.groupId}
                                </ScoopText>
                                <ScoopRow className={styles.rowMeta}>
                                    <ScoopChip
                                        size="small"
                                        color={STATUS_COLORS[group.status] || 'default'}
                                        label={group.status}
                                    />
                                    {formatDate(group.startedAt) && (
                                        <ScoopText className={styles.metaText}>
                                            {formatDate(group.startedAt)}
                                        </ScoopText>
                                    )}
                                </ScoopRow>
                                {active && group.totalScreenings > 0 && (
                                    <div className={styles.progressRow}>
                                        <ScoopLinearProgress
                                            value={Math.round(
                                                ((group.completedScreenings || 0) /
                                                    group.totalScreenings) *
                                                    100
                                            )}
                                            color="primary"
                                            className={styles.progressBar}
                                        />
                                        <ScoopText className={styles.metaText}>
                                            {group.completedScreenings || 0}/
                                            {group.totalScreenings} screenings
                                        </ScoopText>
                                    </div>
                                )}
                            </ScoopColumn>
                            <div className={styles.rowActions}>
                                <ScoopTooltip title="View this group's configuration">
                                    <ScoopButton
                                        variant="grey"
                                        size="xs"
                                        onClick={() => setViewConfigGroup(group)}
                                    >
                                        <FileTextIcon size={14} />
                                    </ScoopButton>
                                </ScoopTooltip>
                                <ScoopButton
                                    variant="grey"
                                    size="xs"
                                    onClick={() => setConfirmGroup(group)}
                                    disabled={active || rerunMutation.isPending}
                                    title="Run this group again with the same configuration"
                                >
                                    <ArrowClockwiseIcon size={14} />
                                    Run again
                                </ScoopButton>
                            </div>
                        </div>
                    )
                })}
            </ScoopColumn>

            <ScoopDialog
                open={Boolean(confirmGroup)}
                onClose={() => setConfirmGroup(null)}
                title="Run again"
                maxWidth="sm"
                actions={
                    <>
                        <ScoopButton variant="grey" onClick={() => setConfirmGroup(null)}>
                            Cancel
                        </ScoopButton>
                        <ScoopButton
                            variant="default"
                            onClick={handleConfirmRerun}
                            disabled={rerunMutation.isPending}
                            loading={rerunMutation.isPending}
                        >
                            Start Run
                        </ScoopButton>
                    </>
                }
            >
                <ScoopText>
                    This starts a full investigation cycle for{' '}
                    <strong>{confirmGroup?.groupName || confirmGroup?.groupId}</strong> using the
                    same configuration as its previous run — the identical pipeline the CLI runs.
                    It consumes AI calls and takes a while.
                </ScoopText>
                {/* The expensive-action caveat is an alert, not a caption (D9). */}
                <ScoopAlert severity="warning" className={styles.cautionAlert}>
                    UI-launched runs execute on AWS Batch (the CLI runs on evo — same code,
                    different machine). Until a Batch run has been proven against a CLI run, treat
                    the output as unverified.
                </ScoopAlert>
            </ScoopDialog>

            <ScoopDialog
                open={Boolean(viewConfigGroup)}
                onClose={() => setViewConfigGroup(null)}
                title={`Configuration — ${viewConfigGroup?.groupName || viewConfigGroup?.groupId || ''}`}
                maxWidth="md"
                actions={
                    <>
                        <ScoopButton variant="grey" onClick={() => setViewConfigGroup(null)}>
                            Close
                        </ScoopButton>
                        <ScoopButton
                            variant="default"
                            disabled={!groupConfig?.configJson}
                            onClick={() => {
                                onNewRun?.(groupConfig?.configJson || '')
                                setViewConfigGroup(null)
                            }}
                        >
                            Start New Run From This
                        </ScoopButton>
                    </>
                }
            >
                {loadingGroupConfig && <ScoopSpinner size={20} />}
                {groupConfigError && (
                    <ScoopText className={styles.error}>{groupConfigError.message}</ScoopText>
                )}
                {groupConfig?.configJson && (
                    <pre className={styles.configJson}>
                        {(() => {
                            try {
                                return JSON.stringify(JSON.parse(groupConfig.configJson), null, 2)
                            } catch {
                                return groupConfig.configJson
                            }
                        })()}
                    </pre>
                )}
            </ScoopDialog>

        </div>
    )
}
